import { type Static, Type } from "@sinclair/typebox"
import {
  CursorQuerySchema,
  createPageSchema,
  IsoDateTimeSchema,
  JsonRecordSchema,
  NodeStatusSchema,
  Nullable,
  ShardInfoSchema,
} from "./common"

// Nodos y sharding — docs/04 §2-§3 y docs/03 §3.

export const NodeSchema = Type.Object({
  id: Type.String(),
  name: Type.String(),
  base_url: Type.String(),
  region: Nullable(Type.String()),
  weight: Type.Integer({ minimum: 0 }),
  // Informativo
  capacity: Nullable(Type.Integer({ minimum: 0 })),
  status: NodeStatusSchema,
  last_heartbeat_at: Nullable(IsoDateTimeSchema),
  meta: JsonRecordSchema,
  // Usuarios asignados (la consola lo muestra por nodo)
  user_count: Type.Optional(Type.Integer({ minimum: 0 })),
  created_at: IsoDateTimeSchema,
  updated_at: IsoDateTimeSchema,
})
export type Node = Static<typeof NodeSchema>

// Directorio público de nodos para el app: resuelve node_id → base_url al iniciar
// sesión. Versión slim del nodo (sin topología interna ni conteos). Cualquier usuario
// autenticado puede leerlo (lo necesita para enrutar telemetría por vehículo).
export const NodeDirectoryEntrySchema = Type.Object({
  id: Type.String(),
  name: Type.String(),
  base_url: Type.String(),
  status: NodeStatusSchema,
})
export type NodeDirectoryEntry = Static<typeof NodeDirectoryEntrySchema>

export const NodeDirectorySchema = Type.Object({
  data: Type.Array(NodeDirectoryEntrySchema),
})
export type NodeDirectory = Static<typeof NodeDirectorySchema>

export const ListNodesQuerySchema = Type.Composite([
  CursorQuerySchema,
  Type.Object({
    status: Type.Optional(NodeStatusSchema),
    region: Type.Optional(Type.String()),
    q: Type.Optional(Type.String()),
  }),
])
export type ListNodesQuery = Static<typeof ListNodesQuerySchema>

export const NodePageSchema = createPageSchema(NodeSchema)
export type NodePage = Static<typeof NodePageSchema>

export const CreateNodeRequestSchema = Type.Object({
  name: Type.String({ minLength: 1 }),
  base_url: Type.String({ minLength: 1 }),
  region: Type.Optional(Type.String()),
  weight: Type.Optional(Type.Integer({ minimum: 0, default: 100 })),
  capacity: Type.Optional(Type.Integer({ minimum: 0 })),
  meta: Type.Optional(JsonRecordSchema),
})
export type CreateNodeRequest = Static<typeof CreateNodeRequestSchema>

export const UpdateNodeRequestSchema = Type.Object({
  name: Type.Optional(Type.String({ minLength: 1 })),
  base_url: Type.Optional(Type.String({ minLength: 1 })),
  region: Type.Optional(Nullable(Type.String())),
  weight: Type.Optional(Type.Integer({ minimum: 0 })),
  capacity: Type.Optional(Nullable(Type.Integer({ minimum: 0 }))),
  status: Type.Optional(NodeStatusSchema),
  meta: Type.Optional(JsonRecordSchema),
})
export type UpdateNodeRequest = Static<typeof UpdateNodeRequestSchema>

// POST /admin/nodes/{id}/drain [V1] — sin confirm devuelve solo el conteo de afectados
// (la consola pide confirmación); con confirm: true inicia el draining.
export const DrainNodeRequestSchema = Type.Object({
  confirm: Type.Optional(Type.Boolean()),
})
export type DrainNodeRequest = Static<typeof DrainNodeRequestSchema>

export const DrainNodeResponseSchema = Type.Object({
  node_id: Type.String(),
  affected_users: Type.Integer({ minimum: 0 }),
  started: Type.Boolean(),
})
export type DrainNodeResponse = Static<typeof DrainNodeResponseSchema>

export const ShardStrategySchema = Type.Union([
  Type.Literal("consistent-hash"),
  Type.Literal("least-loaded"),
  Type.Literal("manual"),
])
export type ShardStrategy = Static<typeof ShardStrategySchema>

export const ShardAssignmentSchema = Type.Object({
  user_id: Type.String(),
  node_id: Type.String(),
  strategy: ShardStrategySchema,
  assigned_at: IsoDateTimeSchema,
  previous_node_id: Nullable(Type.String()),
  reassigned_at: Nullable(IsoDateTimeSchema),
})
export type ShardAssignment = Static<typeof ShardAssignmentSchema>

// GET /admin/shards/assignments?node_id=
export const ListAssignmentsQuerySchema = Type.Composite([
  CursorQuerySchema,
  Type.Object({
    node_id: Type.Optional(Type.String()),
  }),
])
export type ListAssignmentsQuery = Static<typeof ListAssignmentsQuerySchema>

export const ShardAssignmentPageSchema = createPageSchema(ShardAssignmentSchema)
export type ShardAssignmentPage = Static<typeof ShardAssignmentPageSchema>

// POST /admin/shards/rebalance [V1] — dry_run: true simula sin aplicar.
export const RebalanceRequestSchema = Type.Object({
  dry_run: Type.Optional(Type.Boolean()),
})
export type RebalanceRequest = Static<typeof RebalanceRequestSchema>

export const ReassignmentSchema = Type.Object({
  user_id: Type.String(),
  from: Nullable(Type.String()),
  to: Type.String(),
})
export type Reassignment = Static<typeof ReassignmentSchema>

export const RebalanceResponseSchema = Type.Object({
  dry_run: Type.Boolean(),
  total: Type.Integer({ minimum: 0 }),
  reassignments: Type.Array(ReassignmentSchema),
})
export type RebalanceResponse = Static<typeof RebalanceResponseSchema>

// POST /v1/nodes/heartbeat [V1] — client M2M con scope nodes:heartbeat; responde Ok.
export const HeartbeatRequestSchema = Type.Object({
  node_id: Type.String(),
  status: Type.Optional(Type.Union([Type.Literal("active"), Type.Literal("draining")])),
  meta: Type.Optional(JsonRecordSchema),
})
export type HeartbeatRequest = Static<typeof HeartbeatRequestSchema>

// GET /v1/shard/resolve?user_id=… (o ?identifier=…) — exactamente uno de los dos.
export const ShardResolveQuerySchema = Type.Object({
  user_id: Type.Optional(Type.String()),
  identifier: Type.Optional(Type.String()),
})
export type ShardResolveQuery = Static<typeof ShardResolveQuerySchema>

export const ShardResolveResponseSchema = Type.Object({
  user_id: Type.String(),
  shard: ShardInfoSchema,
  strategy: ShardStrategySchema,
  assigned_at: IsoDateTimeSchema,
})
export type ShardResolveResponse = Static<typeof ShardResolveResponseSchema>
